import { gql, useQuery } from "@apollo/client";
import { GravityForm } from "components/GravityForm";
import { Loading } from "features";
import { Page_Flexiblecontent_Blocks_Form } from "graphql";
import { useEffect, useState } from "react";
import Balancer from "react-wrap-balancer";

interface FormProps extends Page_Flexiblecontent_Blocks_Form {
  className?: string;
}

const Form = ({ className, title, content, form, backgroundColor, sectionId }: FormProps) => {
  const { data, loading, error } = useQuery(FORM_QUERY, {
    variables: {
      id: form,
    },
  });

  const [formData, setFormData] = useState({} as any);
  const hasForm = formData && form;

  useEffect(() => {
    if (data) {
      setFormData(data?.gfForm);
    }
  }, [data]);

  return (
    <div id={sectionId as string} className={`${className} relative w-full px-6 py-12 md:px-0 bg-${backgroundColor}`}>
      {title ? (
        <h2 className={`font-heading text-center text-4xl text-primary`}>
          <Balancer>{title}</Balancer>
        </h2>
      ) : null}
      {content ? (
        <div
          className={`text-md mx-auto my-4 max-w-[1280px] text-center font-body text-gray-800`}
          dangerouslySetInnerHTML={{ __html: content ?? `` }}
        />
      ) : null}
      <div
        className={`container mx-auto max-w-[1280px] rounded-xl ${backgroundColor ? `` : `bg-primary-light`} px-4 py-8 text-center`}
      >
        {hasForm && !loading ? (
          <GravityForm form={formData} isLoading={loading} formId={form} />
        ) : (
          <div className={`${className} relative my-12 w-full px-6 md:px-0`}>
            <div className={`container mx-auto text-center`}>
              <Loading />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Form;

const FORM_QUERY = gql`
  query FormQuery($id: ID!) {
    gfForm(id: $id, idType: DATABASE_ID) {
      id
      title
      submitButton {
        text
      }
      description
      confirmations {
        message
      }
      databaseId
      cssClass
      formFields {
        nodes {
          id
          type

          ... on NameField {
            id
            databaseId
            type
            label
            description
            cssClass
            isRequired
            inputs {
              key
              label
              placeholder
              choices {
                text
                value
              }
            }
          }
          ... on EmailField {
            id
            databaseId
            label
            description
            cssClass
            isRequired
            placeholder
          }
          ... on TextField {
            id
            databaseId
            label
            description
            cssClass
            isRequired
            placeholder
          }
          ... on TextAreaField {
            id
            databaseId
            label
            description
            cssClass
            isRequired
            placeholder
          }
          ... on CheckboxField {
            id
            databaseId
            label
            isRequired
            description
            cssClass
            choices {
              isSelected
              text
              value
            }
            inputs {
              id
            }
          }
          ... on MultiSelectField {
            id
            databaseId
            label
            description
            isRequired
            cssClass
            choices {
              isSelected
              text
              value
            }
          }
          ... on SelectField {
            id
            databaseId
            label
            description
            isRequired
            defaultValue
            cssClass
            placeholder
            choices {
              text
              isSelected
              value
            }
          }
          ... on WebsiteField {
            id
            databaseId
            placeholder
            label
            description
            isRequired
            cssClass
            value
          }
          ... on PhoneField {
            id
            databaseId
            label
            description
            cssClass
            isRequired
            placeholder
          }
          ... on DateField {
            id
            databaseId
            label
            description
            cssClass
            isRequired
            placeholder
          }

          ... on NumberField {
            id
            databaseId
            label
            description
            cssClass
            isRequired
            placeholder
          }
          ... on RadioField {
            id
            databaseId
            label
            description
            cssClass
            isRequired
            choices {
              isSelected
              text
              value
            }
          }
          ... on FileUploadField {
            id
            databaseId
            label
            type
            description
            isRequired
            cssClass
          }
          ... on HiddenField {
            id
            databaseId
            inputType
            label
            type
            value
            displayOnly
            defaultValue
            canPrepopulate
            inputName
          }
          ... on HtmlField {
            id
            databaseId
            content
            cssClass
            displayOnly
            hasMargins
            inputType
            label
            layoutGridColumnSpan
            layoutSpacerGridColumnSpan
            pageNumber
            type
            visibility
          }
          ... on AddressField {
            id
            databaseId
            addressType
            addressValues {
              city
              country
              lineTwo
              state
              street
              zip
            }
            value
            type
            label
            adminLabel
            cssClass
            inputName
            inputType
            hasAutocomplete
            isRequired
            inputs {
              placeholder
              name
              label
              key
              isHidden
              id
              defaultValue
              customLabel
              autocompleteAttribute
            }
          }
        }
      }
    }
  }
`;
